import { Form, useNavigation, useNavigate } from "react-router-dom";
import { useState, useCallback, useRef, useContext } from "react";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import DataModal from "../../UI/DataModal";
import ConfirmModal from "../../UI/ConfirmModal";
import { Table } from "react-bootstrap";
import classes from "./CheckUpPatientDetails.module.css";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus, FaEraser, FaMicrophone } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../store/auth-context";

const MedicineDetails = ({ onGetMedicineDetailsHandler, prescrbedMedicines }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const authCtx = useContext(AuthContext);

  // Refs (add form)
  const medicineInput = useRef();
  const dosageInput = useRef();
  const instrInput = useRef();
  const daysInput = useRef();
  const medTypeInput = useRef();

  // State
  const [medicine, setMedicineData] = useState([]);
  const [modalData, setmodalData] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [editRowData, setEditRowData] = useState(null);
  const [selectedMedType, setSelectedMedType] = useState("");
  const [doses, setDoses] = useState({
    breakfast: null,
    lunch: null,
    dinner: null
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [instructionsList, setInstructionsList] = useState([]);
  const [isListeningMedicine, setIsListeningMedicine] = useState(false);
  const [isListeningInstruction, setIsListeningInstruction] = useState(false);

  const handleVoiceInputForMedicine = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert(t("Speech Recognition API is not supported in this browser. Please use Chrome."));
      return;
    }
    if (isListeningMedicine) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-IN';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListeningMedicine(true);
    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript.toLowerCase();
      
      setDoses(prevDoses => {
        const newDoses = { ...prevDoses };
        const timingWords = {
          breakfast: ["breakfast", "morning"],
          lunch: ["lunch", "afternoon"],
          dinner: ["dinner", "night"]
        };

        ['breakfast', 'lunch', 'dinner'].forEach(meal => {
          timingWords[meal].forEach(word => {
            if (transcript.includes(`before ${word}`) || transcript.includes(`${word} before`)) {
               newDoses[meal] = 'BEFORE';
               transcript = transcript.replace(`before ${word}`, '').replace(`${word} before`, '');
            } else if (transcript.includes(`after ${word}`) || transcript.includes(`${word} after`)) {
               newDoses[meal] = 'AFTER';
               transcript = transcript.replace(`after ${word}`, '').replace(`${word} after`, '');
            }
          });
        });

        const medicineName = transcript.replace(/before/g, '').replace(/after/g, '').replace(/\s+/g, ' ').trim().toUpperCase();
        
        if (medicineInput.current) {
           medicineInput.current.value = medicineName;
           handleSearchTermChange(medicineName);
           handleMedicineSelect({ target: { value: medicineName } });
           medicineInput.current.focus();
           setTimeout(() => {
             try {
               if (typeof medicineInput.current.showPicker === 'function') {
                 medicineInput.current.showPicker();
               }
             } catch (err) {
               console.log("Could not open datalist picker:", err);
             }
           }, 500);
        }
        
        return newDoses;
      });
    };
    recognition.onerror = () => setIsListeningMedicine(false);
    recognition.onend = () => setIsListeningMedicine(false);
    recognition.start();
  };

  const handleVoiceInputForInstruction = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert(t("Speech Recognition API is not supported in this browser. Please use Chrome."));
      return;
    }
    if (isListeningInstruction) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-IN';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListeningInstruction(true);
    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript;
      
      if (instrInput.current) {
         instrInput.current.value = transcript;
         fetchInstructionsSuggestions(transcript);
         instrInput.current.focus();
         setTimeout(() => {
           try {
             if (typeof instrInput.current.showPicker === 'function') {
               instrInput.current.showPicker();
             }
           } catch (err) {
             console.log("Could not open datalist picker:", err);
           }
         }, 500);
      }
    };
    recognition.onerror = () => setIsListeningInstruction(false);
    recognition.onend = () => setIsListeningInstruction(false);
    recognition.start();
  };

  const pulseMicStyles = `
    @keyframes pulse-mic {
      0% { transform: scale(1); }
      50% { transform: scale(1.2); }
      100% { transform: scale(1); }
    }
  `;

  const isSubmitting = navigation.state === "submitting";

  // 🔎 Search
  const handleSearchTermChange = useCallback((term) => {
    onSearchMedicineHandler(term);
  }, []);

  const onSearchMedicineHandler = async (term) => {
    const token = getAuthToken();
    if (token === null || token === "EXPIRED") {
      navigate("/");
      return;
    }
    const apiChar = getApiChara();
    const apiUrl =
      `${apiChar}/medicine/getMedicinesByNameOrBarcodeOrDisease?name=` + term;

    const response = await fetch(apiUrl, {
      headers: { Authorization: "Bearer " + token },
    });
    if (!response.ok) throw new Error("Could Not fetch details");

    const data = await response.json();
    setMedicineData(data);
  };

  const handleInstructionChange = (e) => {
    const value = e.target.value;
    fetchInstructionsSuggestions(value);
  };

  const fetchInstructionsSuggestions = async (term) => {
    if (!term || term.trim() === "") {
      setInstructionsList([]);
      return;
    }
    const token = getAuthToken();
    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/instructions/${encodeURIComponent(term)}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch instructions");
      const data = await response.json();
      setInstructionsList(data.map((i) => i.instructionName));
    } catch (error) {
      console.error("Error fetching instruction suggestions:", error);
    }
  };

  // ➕ Add
  const checkUpPatientHandler = () => {
    const prescriptionData = {
      medicineName: medicineInput.current.value.toUpperCase(),
      dosage: dosageInput.current.value,
      instruction: instrInput.current.value,
      days: daysInput.current.value,
      type: medTypeInput.current.value,
      breakFastDose: doses.breakfast,
      lunchDose: doses.lunch,
      dinnerDose: doses.dinner,
      notes: null,
    };

    const hasValues = Object.values(prescriptionData).some(
      (v) => v !== null && v !== ""
    );
    if (!hasValues) return;

    const updated = [...prescrbedMedicines, prescriptionData];
    onGetMedicineDetailsHandler(updated);
    resetForm();
  };

  // 🗑 Delete
  const handleDelete = (index) => {
    const updated = prescrbedMedicines.filter((_, i) => i !== index);
    onGetMedicineDetailsHandler(updated);
  };

  const handleClearAll = () => {
    if (prescrbedMedicines.length === 0) return;
    setShowConfirmModal(true);
  };

  const confirmClearAllHandler = () => {
    onGetMedicineDetailsHandler([]);
    setShowConfirmModal(false);
  };

  // ✏️ Edit
  const handleEdit = (prescription, index) => {
    setEditingIndex(index);
    setEditRowData({ ...prescription });
  };

  const handleRowChange = (e, field) => {
    setEditRowData((prev) => ({
      ...prev,
      [field]: field === "days" ? (e.target.value.replace(/\D/g, "") || "") : e.target.value,
    }));
  };

  const handleSave = (index) => {
    const updated = prescrbedMedicines.map((med, i) =>
      i === index ? editRowData : med
    );
    onGetMedicineDetailsHandler(updated);
    setEditingIndex(null);
    setEditRowData(null);
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditRowData(null);
  };

  const resetForm = () => {
    medicineInput.current.value = "";
    dosageInput.current.value = "";
    instrInput.current.value = "";
    daysInput.current.value = "";
    medTypeInput.current.value = "";
    setDoses({ breakfast: null, lunch: null, dinner: null });
  };

  const fillExistingMedicineHandler = (existingMedData) => {
    // ➕ Append to existing medicines instead of replacing
    const updated = [...(prescrbedMedicines || []), ...existingMedData];
    onGetMedicineDetailsHandler(updated);
  };

  const onDataCloseHandler = () => {
    setmodalData({});
    navigate(-1);
  };
  const handleMedicineSelect = (e) => {
    const selectedName = e.target.value;
    const found = medicine.find((m) => m.name === selectedName);

    // Allowed dropdown values (always uppercase)
    const allowedTypes = [
      "TAB",
      "CAP",
      "SYR",
      "SUSP",
      "SAC",
      "INJ",
      "DROP",
      "EDRP",
      "LIQ",
      "OINT",
      "CRE",
      "GEL",
      "LOT",
      "SPRAY",
      "SUPP",
      "ROTACAP",
      "POWDER"
    ];


    if (found && found.medType) {
      const medTypeUpper = found.medType.toUpperCase(); // 🔑 normalize

      if (allowedTypes.includes(medTypeUpper)) {
        setSelectedMedType(medTypeUpper);
        medTypeInput.current.value = medTypeUpper; // ✅ auto-select correct option
        return;
      }
    }

    // fallback if not valid
    setSelectedMedType("-");
    medTypeInput.current.value = "";
  };

  const renderToggle = (key, label) => (
    <div className={classes.doseBox}>
      <div className={classes.doseLabel}>{t(label)}</div>
      <div className={classes.doseButtons}>
        <button
          type="button"
          className={`${classes.doseBtn} ${doses[key] === 'BEFORE' ? classes.doseBtnActive : ''}`}
          onClick={() => setDoses(prev => ({ ...prev, [key]: prev[key] === 'BEFORE' ? null : 'BEFORE' }))}
        >
          {t("Before")}
        </button>
        <button
          type="button"
          className={`${classes.doseBtn} ${doses[key] === 'AFTER' ? classes.doseBtnActive : ''}`}
          onClick={() => setDoses(prev => ({ ...prev, [key]: prev[key] === 'AFTER' ? null : 'AFTER' }))}
        >
          {t("After")}
        </button>
      </div>
    </div>
  );
  return (
    <>
      <style>{pulseMicStyles}</style>
      {Object.keys(modalData).length > 0 && (
        <DataModal
          prescriptions={modalData}
          OnCloseModalBlock={onDataCloseHandler}
          onAddToMedicineTable={fillExistingMedicineHandler}
        />
      )}

      {/* --- Top Form (unchanged UI) --- */}
      <div className="container-fluid p-0">
        <div className={classes.cardview} style={{ padding: "1rem" }}>
          <Form className={classes.form}>
            <div className="row g-2 align-items-end">
              <div className="col-md-2 col-6">
                <label className={classes.label}>{t("Type")}</label>
                <select ref={medTypeInput}>
                  <option value="" hidden>{t("Select")}</option>
                  <option value="TAB">TAB</option>
                  <option value="CAP">CAP</option>
                  <option value="SYR">SYR</option>
                  <option value="SUSP">SUSP</option>
                  <option value="SAC">SAC</option>
                  <option value="INJ">INJ</option>
                  <option value="DROP">DROP</option>
                  <option value="EDRP">EDRP</option>
                  <option value="LIQ">LIQ</option>
                  <option value="OINT">OINT</option>
                  <option value="CRE">CRE</option>
                  <option value="GEL">GEL</option>
                  <option value="LOT">LOT</option>
                  <option value="POWDER">POWDER</option>
                  <option value="SPRAY">SPRAY</option>
                  <option value="SUPP">SUPP</option>
                  <option value="ROTACAP">ROTACAP</option>
                </select>
              </div>
              <div className="col-md-3 col-6">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <label className={`${classes.label} mb-0`}>{t("Medicine")}</label>
                  {authCtx.hospitalData?.boolOPDVoiceCommand && (
                    <FaMicrophone
                      onClick={handleVoiceInputForMedicine}
                      style={{
                        cursor: "pointer",
                        color: isListeningMedicine ? "#f44336" : "#25d366",
                        animation: isListeningMedicine ? "pulse-mic 1s infinite" : "none"
                      }}
                      title={t("Voice Input")}
                    />
                  )}
                </div>
                <input
                  type="text"
                  placeholder={t("Search Medicine...")}
                  ref={medicineInput}
                  onChange={(e) => {
                    handleSearchTermChange(e.target.value);
                    handleMedicineSelect(e);
                  }}
                  list="medicines-list"
                />
                <datalist id="medicines-list">
                  {medicine.map((m, idx) => (
                    <option key={idx} value={m.name} />
                  ))}
                </datalist>
              </div>
              <div className="col-md-1 col-6">
                <label className={classes.label}>{t("Dosage")}</label>
                <input type="text" ref={dosageInput} />
              </div>
              <div className="col-md-2 col-6">
                {renderToggle('breakfast', 'Breakfast')}
              </div>
              <div className="col-md-2 col-6">
                {renderToggle('lunch', 'Lunch')}
              </div>
              <div className="col-md-2 col-6">
                {renderToggle('dinner', 'Dinner')}
              </div>

              {/* Second Row */}
              <div className="col-md-5 col-12 mt-3">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <label className={`${classes.label} mb-0`}>{t("Instructions")}</label>
                  {authCtx.hospitalData?.boolOPDVoiceCommand && (
                    <FaMicrophone
                      onClick={handleVoiceInputForInstruction}
                      style={{
                        cursor: "pointer",
                        color: isListeningInstruction ? "#f44336" : "#25d366",
                        animation: isListeningInstruction ? "pulse-mic 1s infinite" : "none"
                      }}
                      title={t("Voice Input")}
                    />
                  )}
                </div>
                <input
                  type="text"
                  ref={instrInput}
                  onChange={handleInstructionChange}
                  list="instructions-list"
                />
                <datalist id="instructions-list">
                  {instructionsList.map((inst, idx) => (
                    <option key={idx} value={inst} />
                  ))}
                </datalist>
              </div>
              <div className="col-md-2 col-6 mt-3">
                <label className={classes.label}>{t("Days")}</label>
                <input
                  type="number"
                  ref={daysInput}
                  onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.includes(".")) {
                      e.target.value = parseInt(val);
                    }
                  }}
                />
              </div>
              <div className="col-md-2 col-6 mt-3 d-flex align-items-end">
                <button type="button" className={classes.btnClear} onClick={resetForm}>
                  <FaEraser /> {t("Clear")}
                </button>
              </div>
              <div className="col-md-3 col-12 mt-3 d-flex align-items-end">
                <button type="button" className={classes.btnAdd} onClick={checkUpPatientHandler}>
                  <FaPlus /> {t("Add Medicine")}
                </button>
              </div>
            </div>
          </Form>
        </div>
      </div>

      <hr className="m-2" />

      {/* --- Editable Table --- */}
      <div className="boxes table-responsive">
        <Table striped bordered hover className={classes.prescTable}>
          <thead>
            <tr>
              <th>{t("Sr.No.")}</th>
              <th>{t("Name")}</th>
              <th>{t("Type")}</th>
              <th>{t("Dosage")}</th>
              <th>{t("Breakfast")}</th>
              <th>{t("Lunch")}</th>
              <th>{t("Dinner")}</th>
              <th>{t("Days")}</th>
              <th>{t("Instr")}</th>
              <th className="text-center" style={{ width: "120px" }}>
                <button
                  type="button"
                  className={classes.btnClearAll}
                  onClick={handleClearAll}
                >
                  <FaTrash /> {t("Clear All")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {prescrbedMedicines.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center"> <p className="background-transparent">{t("No medicines prescribed")}</p></td>
              </tr>
            ) : (
              prescrbedMedicines.map((prescription, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  {editingIndex === index ? (
                    <>
                      <td>
                        <input
                          value={editRowData.medicineName}
                          onChange={(e) => handleRowChange(e, "medicineName")}
                          className={classes.preinput}
                        />
                      </td>
                      <td>
                        <select
                          value={editRowData.type}
                          onChange={(e) => handleRowChange(e, "type")}
                          className={classes.preinput}
                        >
                          <option value="">{t("Select")}</option>
                          <option value="TAB">TAB</option>
                          <option value="CAP">CAP</option>
                          <option value="SAC">SAC</option>
                          <option value="LIQ">LIQ</option>
                          <option value="INJ">INJ</option>
                          <option value="OINT">OINT</option>
                          <option value="GEL">GEL</option>
                          <option value="DROP">DROP</option>
                          <option value="SUSP">SUSP</option>
                          <option value="SYR">SYR</option>
                          <option value="POWDER">POWDER</option>
                          <option value="SPRAY">SPRAY</option>
                          <option value="SUPP">SUPP</option>
                        </select>
                      </td>
                      <td>
                        <input
                          value={editRowData.dosage}
                          onChange={(e) => handleRowChange(e, "dosage")}
                          className={classes.preinput}
                          style={{ width: "40px", textAlign: "center" }}
                        />
                      </td>
                      <td>
                        <select
                          value={editRowData.breakFastDose || ""}
                          onChange={(e) => handleRowChange(e, "breakFastDose")}
                          className={classes.preinput}
                        >
                          <option value="">-</option>
                          <option value="BEFORE">BEFORE</option>
                          <option value="AFTER">AFTER</option>
                        </select>
                      </td>
                      <td>
                        <select
                          value={editRowData.lunchDose || ""}
                          onChange={(e) => handleRowChange(e, "lunchDose")}
                          className={classes.preinput}
                        >
                          <option value="">-</option>
                          <option value="BEFORE">BEFORE</option>
                          <option value="AFTER">AFTER</option>
                        </select>
                      </td>
                      <td>
                        <select
                          value={editRowData.dinnerDose || ""}
                          onChange={(e) => handleRowChange(e, "dinnerDose")}
                          className={classes.preinput}
                        >
                          <option value="">-</option>
                          <option value="BEFORE">BEFORE</option>
                          <option value="AFTER">AFTER</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          value={editRowData.days}
                          onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                          onChange={(e) => handleRowChange(e, "days")}
                          className={classes.preinput}
                          style={{ width: "40px", textAlign: "center" }}
                        />
                      </td>
                      <td>
                        <input
                          value={editRowData.instruction}
                          onChange={(e) => handleRowChange(e, "instruction")}
                          className={classes.preinput}
                        />
                      </td>
                      <td>
                        <button className="btn btn-link text-success" onClick={() => handleSave(index)}><FaSave /></button>
                        <button className="btn btn-link text-danger" onClick={handleCancel}><FaTimes /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{prescription?.medicineName}</td>
                      <td>{prescription?.type}</td>
                      <td>{prescription?.dosage}</td>
                      <td>{t(prescription?.breakFastDose || "-")}</td>
                      <td>{t(prescription?.lunchDose || "-")}</td>
                      <td>{t(prescription?.dinnerDose || "-")}</td>
                      <td>{prescription?.days}</td>
                      <td>{prescription?.instruction}</td>
                      <td>
                        <button className="btn btn-link" onClick={() => handleEdit(prescription, index)}><FaEdit /></button>
                        <button className="btn btn-link text-danger" onClick={() => handleDelete(index)}><FaTrash /></button>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={confirmClearAllHandler}
        title={t("Clear All Medicines")}
        message={t("Are you sure you want to clear all added medicines?")}
        variant="danger"
      />
    </>
  );
};

export default MedicineDetails;
